import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { useWorkflowCallbacks } from 'state/WorkflowCallbacksContext';
import { groupActionsByCategory } from 'model/actions';
import { INTERNAL_ACTION_IDS } from 'model/specialActions';
import { useActionsCatalog } from 'hooks/useActionsCatalog';

const StyledRoot = styled.div`
  position: absolute;
  right: -28px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
`;

const StyledTriggerButton = styled.button`
  width: 20px;
  height: 20px;
  border-radius: ${({ theme }) => theme.radii.pill};
  border: 1.5px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.panel};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
  line-height: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition:
    border-color 0.15s,
    color 0.15s,
    box-shadow 0.15s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadows.paletteHover};
  }
`;

const StyledDropdown = styled.div`
  position: absolute;
  top: 50%;
  left: 24px;
  transform: translateY(-50%);
  background: ${({ theme }) => theme.colors.panel};
  border: 1px solid ${({ theme }) => theme.colors.borderMuted};
  border-radius: ${({ theme }) => theme.radii.md};
  box-shadow: ${({ theme }) => theme.shadows.md};
  min-width: 180px;
  max-height: 300px;
  overflow-y: auto;
  padding: 4px 0;
  z-index: ${({ theme }) => theme.zIndex.dropdown};
`;

const StyledLoadingRow = styled.div`
  padding: 8px 12px;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const StyledCategoryHeading = styled.div`
  padding: 6px 12px 2px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const StyledActionButton = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: 5px 12px;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textPrimary};

  &:hover {
    background: ${({ theme }) => theme.colors.primaryFaded};
  }
`;

interface OwnProps {
  stepId: string;
}

const AddNextButtonInner = ({ stepId }: OwnProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { onAddNodeAfter } = useWorkflowCallbacks();
  const { actions } = useActionsCatalog();

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen((v) => !v);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handlePickAction = useCallback(
    (actionId: string) => (e: React.MouseEvent) => {
      e.stopPropagation();
      onAddNodeAfter(stepId, actionId);
      setOpen(false);
    },
    [onAddNodeAfter, stepId],
  );

  const nonInternal = actions.filter((a) => !INTERNAL_ACTION_IDS.has(a.actionId));
  const grouped = groupActionsByCategory(nonInternal);

  return (
    <StyledRoot ref={ref}>
      <StyledTriggerButton
        type='button'
        onClick={handleToggle}
        aria-label='Add next step'
        title='Add next step'
      >
        +
      </StyledTriggerButton>
      {open && (
        <StyledDropdown>
          {actions.length === 0 && <StyledLoadingRow>Loading...</StyledLoadingRow>}
          {Array.from(grouped.entries()).map(([category, catActions]) => (
            <div key={category}>
              <StyledCategoryHeading>{category}</StyledCategoryHeading>
              {catActions.map((action) => (
                <StyledActionButton
                  key={action.actionId}
                  type='button'
                  onClick={handlePickAction(action.actionId)}
                >
                  {action.displayName}
                </StyledActionButton>
              ))}
            </div>
          ))}
        </StyledDropdown>
      )}
    </StyledRoot>
  );
};

const MemoAddNextButton = React.memo(AddNextButtonInner);

export { MemoAddNextButton as AddNextButton };
export default MemoAddNextButton;
